import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Dropdown, Tag, Switch, message, Input, InputNumber, DatePicker } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined, MoreOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import {
  getCouponsApi,
  createCouponApi,
  updateCouponApi,
  deleteCouponApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";
import AppSelect from "../../common/FormInputs/AppSelect";

const ACCENT = "#534ab7";

const DISCOUNT_TYPES = [
  { value: "PERCENTAGE", label: "Percentage (%)" },
  { value: "FLAT",       label: "Flat amount (₹)" },
];

export default function Coupons() {
  const [coupons,  setCoupons]  = useState([]);
  const [search,   setSearch]   = useState("");
  const [open,     setOpen]     = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [discountType, setDiscountType] = useState("PERCENTAGE");
  const [form] = Form.useForm();

  const fetchCoupons = async () => {
    try {
      const res = await getCouponsApi();
      setCoupons((res.data.data || []).slice().sort((a, b) => b.id - a.id));
    } catch {
      message.error("Failed to load coupons");
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const displayed = coupons.filter(
    (c) => !search || c.code?.toLowerCase().includes(search.toLowerCase())
  );

  const isExpired = (c) => c.expiryDate && dayjs(c.expiryDate).isBefore(dayjs(), "day");

  const openAdd = () => {
    setEditing(null);
    setDiscountType("PERCENTAGE");
    form.resetFields();
    form.setFieldsValue({
      discountType: "PERCENTAGE",
      isActive: true,
      minOrderAmount: 0,
    });
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setDiscountType(record.discountType || "PERCENTAGE");
    form.setFieldsValue({
      code:             record.code,
      discountType:     record.discountType,
      discountValue:    record.discountValue,
      minOrderAmount:   record.minOrderAmount,
      maxDiscountAmount:record.maxDiscountAmount,
      expiryDate:       record.expiryDate ? dayjs(record.expiryDate) : null,
      isActive:         record.active,
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      const payload = {
        code:              values.code,
        discountType:      values.discountType,
        discountValue:     values.discountValue,
        minOrderAmount:    values.minOrderAmount ?? 0,
        maxDiscountAmount: values.discountType === "PERCENTAGE" ? (values.maxDiscountAmount ?? null) : null,
        expiryDate:        values.expiryDate ? values.expiryDate.format("YYYY-MM-DD") : null,
        active:            values.isActive,
      };
      if (editing) {
        await updateCouponApi(editing.id, payload);
        message.success("Coupon updated");
      } else {
        await createCouponApi(payload);
        message.success("Coupon added");
      }
      setOpen(false);
      fetchCoupons();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to save coupon");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await updateCouponApi(record.id, {
        code:              record.code,
        discountType:      record.discountType,
        discountValue:     record.discountValue,
        minOrderAmount:    record.minOrderAmount,
        maxDiscountAmount: record.maxDiscountAmount,
        expiryDate:        record.expiryDate,
        active:            !record.active,
      });
      message.success(record.active ? "Coupon deactivated" : "Coupon activated");
      fetchCoupons();
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete this coupon?",
      content: "This cannot be undone. Customers will no longer be able to apply this code.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteCouponApi(id);
          message.success("Coupon deleted");
          fetchCoupons();
        } catch {
          message.error("Failed to delete coupon");
        }
      },
    });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 60,
      render: (v) => <span style={{ color: "#aaa", fontSize: 12 }}>#{v}</span>,
    },
    {
      title: "Code",
      dataIndex: "code",
      render: (v) => <span style={{ fontWeight: 700, color: "#111827", letterSpacing: 0.5 }}>{v}</span>,
    },
    {
      title: "Discount",
      render: (_, r) => (
        <span>
          {r.discountType === "PERCENTAGE" ? `${r.discountValue}%` : `₹${r.discountValue}`}
          {r.discountType === "PERCENTAGE" && r.maxDiscountAmount
            ? <span style={{ color: "#9CA3AF", fontSize: 12 }}> (max ₹{r.maxDiscountAmount})</span>
            : null}
        </span>
      ),
    },
    {
      title: "Min Order",
      dataIndex: "minOrderAmount",
      render: (v) => `₹${v || 0}`,
    },
    {
      title: "Expiry",
      dataIndex: "expiryDate",
      render: (v, r) => v
        ? <Tag color={isExpired(r) ? "red" : "default"}>{dayjs(v).format("DD MMM YYYY")}</Tag>
        : <span style={{ color: "#ccc" }}>No expiry</span>,
    },
    {
      title: "Status",
      dataIndex: "active",
      width: 110,
      align: "center",
      render: (v, r) => isExpired(r)
        ? <Tag color="red">Expired</Tag>
        : v ? <Tag color="green">Active</Tag> : <Tag color="default">Off</Tag>,
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(r) },
              {
                key: "toggle",
                label: r.active ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Coupons</div>
          <div className="page-sub">{coupons.length} coupons — only active, unexpired coupons are valid at checkout</div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ background: ACCENT, border: "none", borderRadius: 8 }}
          onClick={openAdd}
        >
          Add Coupon
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search coupon codes…"
          allowClear
          style={{ width: 280, borderRadius: 8 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <AppTable columns={columns} data={displayed} />

      <Modal
        title={editing ? `Edit — ${editing.code}` : "Add Coupon"}
        open={open}
        okText={editing ? "Save Changes" : "Add Coupon"}
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading }}
        onOk={handleSubmit}
        onCancel={() => { setOpen(false); setEditing(null); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <AppInput
            name="code"
            label="Coupon Code"
            rules={[{ required: true, message: "Code is required" }]}
            placeholder="e.g. WELCOME10"
            style={{ textTransform: "uppercase" }}
          />

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <AppSelect
                name="discountType"
                label="Discount Type"
                rules={[{ required: true, message: "Pick a discount type" }]}
                options={DISCOUNT_TYPES}
                onChange={(v) => setDiscountType(v)}
              />
            </div>
            <Form.Item
              name="discountValue"
              label={discountType === "PERCENTAGE" ? "Percentage" : "Amount (₹)"}
              rules={[{ required: true, message: "Required" }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={1} max={discountType === "PERCENTAGE" ? 100 : undefined} style={{ width: "100%" }} />
            </Form.Item>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="minOrderAmount" label="Min Order Amount (₹)" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: "100%" }} placeholder="0" />
            </Form.Item>
            {discountType === "PERCENTAGE" && (
              <Form.Item name="maxDiscountAmount" label="Max Discount Cap (₹)" style={{ flex: 1 }}>
                <InputNumber min={0} style={{ width: "100%" }} placeholder="No cap" />
              </Form.Item>
            )}
          </div>

          <Form.Item name="expiryDate" label="Expiry Date">
            <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" disabledDate={(d) => d && d.isBefore(dayjs(), "day")} />
          </Form.Item>

          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
